/**
 * PillWithDetail
 *
 * Shared "tap for more" behavior for colored trait/preference pills whose
 * label text carries a parenthetical detail (e.g. "Spicy (enjoys mild heat)").
 * Splits the label at the first '(' and, when a detail is present, wraps the
 * caller-supplied pill visual in an accessible Popover trigger — the whole
 * pill becomes the tap target, not just the info glyph.
 */
import { Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverTrigger, Box, HStack, Text } from '@chakra-ui/react'
import { Info } from 'lucide-react'
import type { ReactNode } from 'react'
import { softGlowBoxShadow } from '@/styles/glassCard'

export interface SplitPillLabel {
  /** The short label the pill displays (parenthetical stripped). */
  label: string
  /** Parenthetical detail text, parens stripped — null when the pill has none. */
  detail: string | null
}

/**
 * Splits a raw pill label at its first '(' into a display label and an
 * optional detail string.
 *
 * - No '(' present → untreated (`detail: null`, `label` is the raw string).
 * - Label would be empty (string starts with '(') → untreated.
 * - Multiple '(...)' groups → detail spans from the first '(' to the end of
 *   the LAST ')'.
 * - Missing closing ')' (LLM truncation) → detail is everything after the
 *   first '('.
 */
export function splitPillLabel(raw: string): SplitPillLabel {
  const openIndex = raw.indexOf('(')
  if (openIndex <= 0) {
    return { label: raw, detail: null }
  }

  const label = raw.slice(0, openIndex).trim()
  if (!label) {
    return { label: raw, detail: null }
  }

  const closeIndex = raw.lastIndexOf(')')
  const rawDetail = closeIndex > openIndex
    ? raw.slice(openIndex + 1, closeIndex)
    : raw.slice(openIndex + 1)
  const detail = rawDetail.trim()

  return detail ? { label, detail } : { label: raw, detail: null }
}

interface PillWithDetailProps {
  /** Raw label text, possibly containing a parenthetical detail. */
  rawLabel: string
  /** Renders the pill's own visual (Badge, etc.) given the display label and whether a detail affordance should be shown. */
  children: (displayLabel: string, hasDetail: boolean) => ReactNode
}

/**
 * Wraps a pill in a Popover trigger when its label has a parenthetical
 * detail. Pills without one render exactly as `children` produces them —
 * no wrapper, no cursor change.
 */
export function PillWithDetail({ rawLabel, children }: PillWithDetailProps) {
  const { label, detail } = splitPillLabel(rawLabel)

  if (!detail) {
    return <>{children(label, false)}</>
  }

  return (
    <Popover placement="top" trigger="click" isLazy>
      <PopoverTrigger>
        <Box
          as="button"
          type="button"
          aria-label={`Show more about ${label}`}
          display="inline-flex"
          sx={{ background: 'none', border: 'none', padding: 0, margin: 0, font: 'inherit', cursor: 'pointer' }}
          _focusVisible={{ outline: '2px solid', outlineColor: 'whiteAlpha.600', outlineOffset: '2px', borderRadius: 'full' }}
        >
          {children(label, true)}
        </Box>
      </PopoverTrigger>
      <PopoverContent
        // D45b Phase 6 (FR-N9, NFR-4): not a roster surface (this is a
        // profile-page detail popover, not one of the six named roster
        // categories) — backdropFilter removed; var(--color-overlay-90) is
        // already 90% opaque, plenty for legibility without blur.
        bg="var(--color-overlay-90)"
        border="1px solid"
        borderColor="whiteAlpha.300"
        boxShadow={softGlowBoxShadow}
        borderRadius="lg"
        maxW="260px"
        w="auto"
        _focus={{ boxShadow: softGlowBoxShadow }}
      >
        <PopoverArrow bg="var(--color-overlay-90)" />
        <PopoverBody px={3} py={2}>
          <Text fontSize="sm" color="whiteAlpha.900">
            {detail}
          </Text>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

/** Small "tap for more" affordance appended inside a pill when it has a detail. */
export function PillInfoAffix() {
  return (
    <HStack
      as="span"
      spacing={1}
      ml={1.5}
      pl={1.5}
      borderLeft="1px solid"
      borderColor="whiteAlpha.400"
      display="inline-flex"
      alignItems="center"
    >
      <Info size={13} strokeWidth={2.5} opacity={0.85} />
    </HStack>
  )
}
